/**
 * Editors for the City Satisfaction settings GUS's flag manifest does not
 * describe (`modules.atlas.tabs.satisfaction.config.*`).
 *
 * Each editor writes exactly one key, through the same `onChange(path, value)`
 * the manifest tree uses, and writes null to hand a key back to GUS's default.
 * What is wrong with a value comes from satisfactionSettings.ts, which is the
 * same check the backend runs before it saves; nothing here re-states a rule.
 *
 * `civic_domains` is not here: the manifest describes it, so the tree above
 * already draws its list editor, and the shared check covers it on save.
 */
import React, { useState } from 'react';
import styled, { css } from 'styled-components';
import { spacing, typography, borderRadius, brandColors } from '../../design-system/tokens';
import {
  Bilingual, CENSUS_FIELDS, DEFAULT_LOVED, DEFAULT_LOVED_GROUPS, DIALECT_SIDES, GAP_MAX_CHARS, GAP_MAX_ITEMS,
  LOVED_GROUP_IDS, LovedGroup, PRAISE_REASONS, RAISED_REASONS, ReasonOption, SATISFACTION_PATH, SettingError,
  charCount, effectiveMinN, satisfactionErrors,
} from './satisfactionSettings';

const CONFIG = `${SATISFACTION_PATH}.config`;

/** The keys this panel edits, so the tree does not list them as unknown. */
export const PANEL_KEYS = [
  'grey_below', 'loved_reasons', 'loved_reason_groups', 'census_columns', 'dialects',
  'taxonomy_gaps', 'census_min_count', 'place_quality_raised',
];

interface Props {
  storedFlags: Record<string, any>;
  onChange: (path: string, value: any) => void;
  disabled?: boolean;
}

// =============================================================================
// STYLED COMPONENTS -- the leaf controls' voice (flags/FlagLeafControl.tsx)
// =============================================================================

const focusRing = css`
  &:focus-visible {
    outline: 2px solid ${props => props.theme?.colors?.accent?.primary || brandColors.silaTeal};
    outline-offset: 2px;
  }
`;

const fieldSurface = css`
  font-family: inherit;
  font-size: ${typography.fontSize.sm};
  color: ${props => props.theme?.colors?.text?.primary || '#ffffff'};
  background: ${props => props.theme?.colors?.background?.secondary || 'rgba(26, 26, 26, 0.5)'};
  border: 1px solid ${props => props.theme?.colors?.border?.primary || 'rgba(255, 255, 255, 0.1)'};
  border-radius: ${borderRadius.md};
  padding: ${spacing['1.5']} ${spacing['2']};
  max-width: 100%;
  &:focus { outline: none; border-color: ${props => props.theme?.colors?.accent?.primary || brandColors.silaTeal}; }
  ${focusRing}
  &:disabled { opacity: 0.5; cursor: not-allowed; }
`;

const Panel = styled.section`
  display: flex;
  flex-direction: column;
  gap: ${spacing['4']};
  min-width: 0;
`;

const Setting = styled.div<{ $error: boolean }>`
  display: flex;
  flex-direction: column;
  gap: ${spacing['1.5']};
  min-width: 0;
  padding-left: ${spacing['3']};
  border-left: 2px solid ${props => (props.$error
    ? props.theme?.colors?.accent?.danger || brandColors.danger
    : props.theme?.colors?.border?.secondary || 'rgba(255, 255, 255, 0.08)')};
`;

const TitleRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: baseline;
  gap: ${spacing['1']} ${spacing['2']};
`;

const Title = styled.h4`
  margin: 0;
  font-size: ${typography.fontSize.sm};
  font-weight: ${typography.fontWeight.semibold};
  color: ${props => props.theme?.colors?.text?.primary || '#ffffff'};
`;

const Arabic = styled.span`
  font-size: ${typography.fontSize.xs};
  color: ${props => props.theme?.colors?.text?.tertiary || '#64748b'};
`;

const Key = styled.code`
  font-family: ${typography.fontFamily.mono};
  font-size: ${typography.fontSize.xs};
  color: ${props => props.theme?.colors?.text?.secondary || '#94a3b8'};
`;

const State = styled.span`
  font-size: ${typography.fontSize.xs};
  color: ${props => props.theme?.colors?.text?.secondary || '#94a3b8'};
`;

const Help = styled.p`
  margin: 0;
  max-width: 640px;
  font-size: ${typography.fontSize.xs};
  line-height: ${typography.lineHeight.normal};
  color: ${props => props.theme?.colors?.text?.secondary || '#94a3b8'};
`;

const Note = styled.p<{ $tone: 'error' | 'warning' }>`
  margin: 0;
  max-width: 640px;
  font-size: ${typography.fontSize.xs};
  line-height: ${typography.lineHeight.normal};
  color: ${props => (props.$tone === 'error'
    ? props.theme?.colors?.accent?.danger || brandColors.danger
    : props.theme?.colors?.accent?.warning || brandColors.warning)};
`;

const Row = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: ${spacing['1']} ${spacing['2']};
  min-width: 0;
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 200px) minmax(0, 220px);
  align-items: center;
  gap: ${spacing['1.5']} ${spacing['3']};
`;

const FieldLabel = styled.label`
  display: flex;
  flex-direction: column;
  font-size: ${typography.fontSize.xs};
  color: ${props => props.theme?.colors?.text?.primary || '#ffffff'};
`;

const Input = styled.input`
  ${fieldSurface}
  width: 100%;
`;

const NumberInput = styled.input`
  ${fieldSurface}
  width: 110px;
`;

const Select = styled.select`
  ${fieldSurface}
`;

const Chip = styled.label<{ $checked: boolean }>`
  display: inline-flex;
  align-items: center;
  gap: ${spacing['1']};
  padding: ${spacing['1']} ${spacing['2']};
  font-size: ${typography.fontSize.xs};
  color: ${props => (props.$checked ? props.theme?.colors?.text?.primary || '#ffffff' : props.theme?.colors?.text?.secondary || '#94a3b8')};
  background: ${props => (props.$checked ? `${props.theme?.colors?.accent?.primary || brandColors.silaTeal}20` : 'transparent')};
  border: 1px solid ${props => (props.$checked
    ? props.theme?.colors?.accent?.primary || brandColors.silaTeal
    : props.theme?.colors?.border?.secondary || 'rgba(255, 255, 255, 0.1)')};
  border-radius: ${borderRadius.sm};
  cursor: pointer;
  &:has(input:focus-visible) {
    outline: 2px solid ${props => props.theme?.colors?.accent?.primary || brandColors.silaTeal};
    outline-offset: 2px;
  }
`;

const HiddenCheckbox = styled.input`
  position: absolute;
  width: 1px;
  height: 1px;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  border: 0;
`;

const SmallButton = styled.button`
  ${fieldSurface}
  font-size: ${typography.fontSize.xs};
  padding: ${spacing['1']} ${spacing['2']};
  cursor: pointer;
`;

const Remove = styled.button`
  padding: 0 ${spacing['1']};
  font-size: ${typography.fontSize.xs};
  color: inherit;
  background: transparent;
  border: none;
  cursor: pointer;
  ${focusRing}
`;

// =============================================================================
// PIECES
// =============================================================================

const isPlain = (v: unknown): v is Record<string, any> => v !== null && typeof v === 'object' && !Array.isArray(v);
const reasonLabel = (opts: readonly ReasonOption[], id: string) => opts.find((r) => r.id === id);

const Heading: React.FC<{ en: string; ar: string; k: string; usingDefault?: Bilingual }> = ({ en, ar, k, usingDefault }) => (
  <TitleRow>
    <Title>{en}</Title>
    <Arabic lang="ar" dir="rtl">{ar}</Arabic>
    <Key>{k}</Key>
    {usingDefault && (
      <State>
        {usingDefault.en} <span lang="ar" dir="rtl">/ {usingDefault.ar}</span>
      </State>
    )}
  </TitleRow>
);

/** One line in both languages. */
const Both: React.FC<Bilingual> = ({ en, ar }) => (
  <>
    {en} <span lang="ar" dir="rtl">/ {ar}</span>
  </>
);

const Errors: React.FC<{ errors: SettingError[] }> = ({ errors }) => (
  <>
    {errors.map((e) => (
      <Note key={`${e.rule}-${e.path}`} $tone="error" role="alert">
        {e.message.en} <span lang="ar" dir="rtl">{e.message.ar}</span>
      </Note>
    ))}
  </>
);

/**
 * Digits become a number; empty is null ("use GUS's default"); anything else is
 * kept as the text it is, so the check shows it in red instead of it quietly
 * turning into "not set". The fields are text, not type="number": a number field
 * reports text it cannot read as empty, which would clear the setting unseen.
 */
const parseCount = (raw: string): number | string | null => {
  // Arabic-Indic and Eastern Arabic-Indic digits, as an Arabic keyboard types them, read as the digits they are.
  const t = raw.trim().replace(/[\u0660-\u0669\u06f0-\u06f9]/g, (d) => String(d.charCodeAt(0) & 0xf));
  if (t === '') return null;
  return /^[0-9]+$/.test(t) ? Number(t) : raw.trim();
};

// =============================================================================
// PANEL
// =============================================================================

const SatisfactionSettingsPanel: React.FC<Props> = ({ storedFlags, onChange, disabled = false }) => {
  const cfg: Record<string, any> = isPlain(storedFlags?.modules?.atlas?.tabs?.satisfaction?.config)
    ? storedFlags.modules.atlas.tabs.satisfaction.config
    : {};
  const errors = satisfactionErrors(storedFlags);
  const errorsFor = (key: string) => errors.filter((e) => e.path === `${CONFIG}.${key}` || e.path.startsWith(`${CONFIG}.${key}.`));
  const set = (key: string, value: any) => onChange(`${CONFIG}.${key}`, value);
  const absent = (key: string) => cfg[key] === undefined || cfg[key] === null;

  const [greyDraft, setGreyDraft] = useState('');
  const [greyNote, setGreyNote] = useState<Bilingual | null>(null);
  const [gapDraft, setGapDraft] = useState<Record<string, string>>({ en: '', ar: '' });
  const [gapNote, setGapNote] = useState<Record<string, Bilingual | null>>({ en: null, ar: null });

  // --- grey_below -----------------------------------------------------------
  const greyStored = cfg.grey_below;
  const grey: unknown[] = Array.isArray(greyStored) ? greyStored : absent('grey_below') ? [50, 100] : [];
  const minN = effectiveMinN(cfg);
  const addGrey = () => {
    const v = parseCount(greyDraft);
    if (v === null) return;
    // Nothing the draft box holds is dropped without a word: it is added, or the box says why not.
    if (typeof v !== 'number' || v <= minN) {
      setGreyNote({
        en: `Type a whole number of posts above the map's minimum (${minN}).`,
        ar: `اكتب عددًا صحيحًا من المنشورات أكبر من الحد الأدنى للخريطة (${minN}).`,
      });
      return;
    }
    if (grey.includes(v)) {
      setGreyNote({ en: `${v} is already a choice.`, ar: `${v} موجود بالفعل.` });
      return;
    }
    const next = [...grey, v];
    // Sorted as GUS sorts them, so what is saved reads as the map will list it.
    next.sort((a, b) => (typeof a === 'number' && typeof b === 'number' ? a - b : 0));
    set('grey_below', next);
    setGreyDraft('');
    setGreyNote(null);
  };

  // --- loved_reasons / loved_reason_groups ----------------------------------
  const lovedStored: unknown = cfg.loved_reasons;
  const loved: string[] = Array.isArray(lovedStored) ? lovedStored.filter((x): x is string => typeof x === 'string') : [...DEFAULT_LOVED];
  const toggleLoved = (id: string) => {
    const next = loved.includes(id) ? loved.filter((x) => x !== id) : [...loved, id];
    set('loved_reasons', next);
  };
  const newLoved = loved.filter((id) => !DEFAULT_LOVED.includes(id));

  const groupsStored: unknown = cfg.loved_reason_groups;
  const groups: Record<string, string[]> = isPlain(groupsStored)
    ? Object.fromEntries(Object.entries(groupsStored).map(([k, v]) => [k, Array.isArray(v) ? v : []]))
    : Object.fromEntries(LOVED_GROUP_IDS.map((g) => [g, DEFAULT_LOVED_GROUPS[g].filter((id) => loved.includes(id))]));
  const groupOf = (id: string): LovedGroup | '' =>
    (LOVED_GROUP_IDS.find((g) => (groups[g] || []).includes(id)) as LovedGroup | undefined) || '';
  const setGroup = (id: string, group: LovedGroup | '') => {
    const next: Record<string, string[]> = Object.fromEntries(
      LOVED_GROUP_IDS.map((g) => [g, (groups[g] || []).filter((x) => x !== id)]),
    );
    if (group) next[group] = [...next[group], id];
    // Keep any key the admin cannot see here, so the check can name it rather than it vanishing.
    set('loved_reason_groups', { ...(isPlain(groupsStored) ? groupsStored : {}), ...next });
  };
  // Ids named in a group that are no longer loved reasons, shown so the admin can take them out.
  const groupStrays = Object.values(groups).flat().filter((id) => !loved.includes(id));

  // --- census_columns -------------------------------------------------------
  const columns: Record<string, any> = isPlain(cfg.census_columns) ? cfg.census_columns : {};
  const setColumn = (field: string, raw: string) => {
    const next = { ...columns };
    if (raw.trim() === '') delete next[field];
    else next[field] = raw.trim();
    set('census_columns', Object.keys(next).length ? next : null);
  };

  // --- dialects -------------------------------------------------------------
  const dialects: Record<string, any> = isPlain(cfg.dialects) ? cfg.dialects : {};
  const setDialect = (side: string, raw: string) => {
    const next = { ...dialects, [side]: raw };
    // Both sides cleared is "no comparison", whatever else the stored value held.
    const empty = DIALECT_SIDES.every((s) => typeof next[s] !== 'string' || next[s].trim() === '');
    set('dialects', empty ? null : next);
  };

  // --- taxonomy_gaps --------------------------------------------------------
  const gaps: Record<string, any> = isPlain(cfg.taxonomy_gaps) ? cfg.taxonomy_gaps : {};
  const gapList = (lang: string): string[] => (Array.isArray(gaps[lang]) ? gaps[lang] : []);
  const writeGaps = (lang: string, items: string[]) => {
    const next = { ...gaps, [lang]: items };
    if (!items.length) delete next[lang];
    set('taxonomy_gaps', Object.keys(next).length ? next : null);
  };

  // --- census_min_count -----------------------------------------------------
  const floor = cfg.census_min_count;

  // --- place_quality_raised -------------------------------------------------
  const pq: string[] = Array.isArray(cfg.place_quality_raised)
    ? cfg.place_quality_raised.filter((x: unknown): x is string => typeof x === 'string')
    : [];
  const togglePq = (id: string) => {
    const next = pq.includes(id) ? pq.filter((x) => x !== id) : [...pq, id];
    set('place_quality_raised', next.length ? next : null);
  };

  const defaultButton = (key: string) =>
    !absent(key) && (
      <SmallButton type="button" disabled={disabled} onClick={() => set(key, null)}>
        <Both en="Use GUS default" ar="استخدم الإعداد الافتراضي في GUS" />
      </SmallButton>
    );

  return (
    <Panel aria-label="City Satisfaction settings">
      <Help>
        Settings GUS reads for City Satisfaction that its flag list does not describe. GUS ignores a value it
        cannot use and quietly falls back to its default, so a value shown in red is one GUS would ignore. Save
        refuses a red value you add or change; one that was already stored, and is left as it was, does not block
        the save. Use GUS default clears it.
        <Arabic lang="ar" dir="rtl" style={{ display: 'block' }}>
          إعدادات يقرؤها GUS لرضا المدينة ولا تصفها قائمة مفاتيحه. يتجاهل GUS أي قيمة لا يستطيع استخدامها ويعود
          بصمت إلى قيمته الافتراضية، لذا فالقيمة المعروضة بالأحمر قيمة سيتجاهلها GUS. يرفض الحفظ أي قيمة حمراء
          تضيفها أو تغيّرها؛ أما القيمة المخزّنة سابقًا والمتروكة كما هي فلا تمنع الحفظ. زر «استخدم الإعداد
          الافتراضي في GUS» يمسحها.
        </Arabic>
      </Help>

      {/* grey_below */}
      <Setting $error={errorsFor('grey_below').length > 0}>
        <Heading en="Grey below (map)" ar="رمادي تحت (الخريطة)" k="config.grey_below"
          usingDefault={absent('grey_below') ? { en: 'GUS default: 50, 100', ar: 'افتراضي GUS: 50، 100' } : undefined} />
        <Help>
          The extra post counts the map offers under &quot;Grey below&quot;: a place with fewer posts than the chosen
          count is drawn grey. The map always offers its minimum post count ({minN}) first, and only choices above
          it. An empty list offers no extra choices.
        </Help>
        <Row>
          {grey.map((x, i) => (
            // eslint-disable-next-line react/no-array-index-key -- a stored list may repeat a value
            <Chip key={`${i}-${String(x)}`} $checked as="span">
              {String(x)}
              <Remove type="button" disabled={disabled} aria-label={`remove ${String(x)}`}
                onClick={() => set('grey_below', grey.filter((_, j) => j !== i))}>×</Remove>
            </Chip>
          ))}
          <NumberInput type="text" inputMode="numeric" disabled={disabled} value={greyDraft} placeholder="posts"
            aria-label="add a Grey below choice"
            onChange={(e) => { setGreyDraft(e.target.value); setGreyNote(null); }}
            onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addGrey(); } }} />
          <SmallButton type="button" disabled={disabled || !greyDraft.trim()} onClick={addGrey}
            aria-label="Add Grey below choice"><Both en="Add" ar="إضافة" /></SmallButton>
          {defaultButton('grey_below')}
        </Row>
        {greyNote && <Note $tone="warning" role="status"><Both {...greyNote} /></Note>}
        <Errors errors={errorsFor('grey_below')} />
      </Setting>

      {/* loved_reasons */}
      <Setting $error={errorsFor('loved_reasons').length > 0}>
        <Heading en="Loved for" ar="محبوب لأجل" k="config.loved_reasons"
          usingDefault={absent('loved_reasons') ? { en: 'GUS default: the six marked', ar: 'افتراضي GUS: الستة المحددة' } : undefined} />
        <Help>What a place can be &quot;loved for&quot; on the Loved page and in the city facts.</Help>
        <Row>
          {PRAISE_REASONS.map((r) => (
            <Chip key={r.id} $checked={loved.includes(r.id)}>
              <HiddenCheckbox type="checkbox" checked={loved.includes(r.id)} disabled={disabled}
                onChange={() => toggleLoved(r.id)} aria-label={r.en} />
              {r.en} <Arabic lang="ar" dir="rtl">{r.ar}</Arabic>
            </Chip>
          ))}
          {defaultButton('loved_reasons')}
        </Row>
        {newLoved.length > 0 && (
          <Note $tone="warning">
            Not in GUS&apos;s default set: {newLoved.map((id) => reasonLabel(PRAISE_REASONS, id)?.en || id).join(', ')}.
            GUS has &quot;loved for&quot; wording for it, but it has not been checked in this sentence and has no short
            form for the page&apos;s opening line, which then leaves it out. Check the Loved page in English and
            Arabic before relying on it.
            <Arabic lang="ar" dir="rtl" style={{ display: 'block' }}>
              ليس ضمن المجموعة الافتراضية في GUS: {newLoved.map((id) => reasonLabel(PRAISE_REASONS, id)?.ar || id).join('، ')}.
              لدى GUS صياغة «محبوب لأجل» له، لكنها لم تُراجَع في هذه الجملة، وليس له صيغة مختصرة لسطر افتتاح الصفحة
              فيُحذف منه. راجع صفحة «المحبوب» بالإنجليزية والعربية قبل الاعتماد عليه.
            </Arabic>
          </Note>
        )}
        <Errors errors={errorsFor('loved_reasons')} />
      </Setting>

      {/* loved_reason_groups */}
      <Setting $error={errorsFor('loved_reason_groups').length > 0}>
        <Heading en="Loved for: groups" ar="محبوب لأجل: المجموعات" k="config.loved_reason_groups"
          usingDefault={absent('loved_reason_groups') ? { en: 'GUS default', ar: 'افتراضي GUS' } : undefined} />
        <Help>
          The Loved page sets the reasons in two groups. A reason in neither group is shown under &quot;Other&quot;.
          Each reason belongs to one group at most.
        </Help>
        <Grid>
          {loved.map((id, i) => {
            const r = reasonLabel(PRAISE_REASONS, id);
            return (
              // eslint-disable-next-line react/no-array-index-key -- a stored list may repeat an id
              <React.Fragment key={`${i}-${id}`}>
                <FieldLabel htmlFor={`loved-group-${i}-${id}`}>
                  {r?.en || id} <Arabic lang="ar" dir="rtl">{r?.ar}</Arabic>
                </FieldLabel>
                <Select id={`loved-group-${i}-${id}`} disabled={disabled} value={groupOf(id)}
                  onChange={(e) => setGroup(id, e.target.value as LovedGroup | '')}>
                  <option value="offers">What it offers / ما يقدّمه</option>
                  <option value="look_and_feel">How it looks and feels / مظهره وأجواؤه</option>
                  <option value="">Other / أخرى</option>
                </Select>
              </React.Fragment>
            );
          })}
        </Grid>
        {groupStrays.length > 0 && (
          <Row>
            <Note $tone="warning">
              <Both en={`In a group but no longer a loved reason: ${groupStrays.join(', ')}.`}
                ar={`في مجموعة لكنه لم يعد من أسباب التفضيل: ${groupStrays.join('، ')}.`} />
            </Note>
            {Array.from(new Set(groupStrays)).map((id) => (
              <SmallButton key={id} type="button" disabled={disabled} onClick={() => setGroup(id, '')}>
                <Both en={`Take ${id} out of its group`} ar={`أخرج ${id} من مجموعته`} />
              </SmallButton>
            ))}
          </Row>
        )}
        <Row>{defaultButton('loved_reason_groups')}</Row>
        <Errors errors={errorsFor('loved_reason_groups')} />
      </Setting>

      {/* census_columns */}
      <Setting $error={errorsFor('census_columns').length > 0}>
        <Heading en="Census columns" ar="أعمدة التعداد" k="config.census_columns"
          usingDefault={absent('census_columns') ? { en: 'GUS default: the usual column names', ar: 'افتراضي GUS: أسماء الأعمدة المعتادة' } : undefined} />
        <Help>
          For the &quot;who lives there&quot; figures on the Who page. Name a column only when this tenant&apos;s census
          table does not use the usual name. Lower-case letters, digits and underscores.
        </Help>
        <Grid>
          {CENSUS_FIELDS.map((field) => (
            <React.Fragment key={field}>
              <FieldLabel htmlFor={`census-${field}`}>
                {CENSUS_FIELD_LABELS[field]?.en || field}
                <Arabic lang="ar" dir="rtl">{CENSUS_FIELD_LABELS[field]?.ar}</Arabic>
              </FieldLabel>
              <Input id={`census-${field}`} type="text" disabled={disabled} placeholder="usual name"
                value={typeof columns[field] === 'string' ? columns[field] : ''}
                onChange={(e) => setColumn(field, e.target.value)} />
            </React.Fragment>
          ))}
        </Grid>
        <Row>{defaultButton('census_columns')}</Row>
        <Errors errors={errorsFor('census_columns')} />
      </Setting>

      {/* dialects */}
      <Setting $error={errorsFor('dialects').length > 0}>
        <Heading en="Dialects compared" ar="اللهجتان المقارنتان" k="config.dialects"
          usingDefault={absent('dialects') ? { en: 'not set: no dialect comparison', ar: 'غير مضبوط: لا مقارنة بين اللهجات' } : undefined} />
        <Help>
          The two dialects the Who page compares, written exactly as this tenant stores them (or a dialect group id).
          Set two different ones, and only with the data team&apos;s confirmation. GUS also cannot compare a writing
          register or a &quot;not recorded&quot; value; this tenant&apos;s data decides which those are.
        </Help>
        <Grid>
          <FieldLabel htmlFor="dialect-national">National <Arabic lang="ar" dir="rtl">الوطنية</Arabic></FieldLabel>
          <Input id="dialect-national" type="text" disabled={disabled}
            value={typeof dialects.national === 'string' ? dialects.national : ''}
            onChange={(e) => setDialect('national', e.target.value)} />
          <FieldLabel htmlFor="dialect-reference">Reference <Arabic lang="ar" dir="rtl">المرجعية</Arabic></FieldLabel>
          <Input id="dialect-reference" type="text" disabled={disabled}
            value={typeof dialects.reference === 'string' ? dialects.reference : ''}
            onChange={(e) => setDialect('reference', e.target.value)} />
        </Grid>
        <Row>{defaultButton('dialects')}</Row>
        <Errors errors={errorsFor('dialects')} />
      </Setting>

      {/* taxonomy_gaps */}
      <Setting $error={errorsFor('taxonomy_gaps').length > 0}>
        <Heading en="Topics not covered" ar="مواضيع غير مشمولة" k="config.taxonomy_gaps"
          usingDefault={absent('taxonomy_gaps') ? { en: 'not set: no "not measured" line', ar: 'غير مضبوط: لا سطر «غير مقيس»' } : undefined} />
        <Help>
          Short notes naming what the data does not measure, shown as one &quot;not measured&quot; line on the Why page.
          Up to {GAP_MAX_ITEMS} per language, each up to {GAP_MAX_CHARS} characters.
        </Help>
        {(['en', 'ar'] as const).map((lang) => {
          const items = gapList(lang);
          const note = (n: Bilingual | null) => setGapNote((g) => ({ ...g, [lang]: n }));
          const add = () => {
            const t = (gapDraft[lang] || '').trim();
            if (!t) return;
            // Nothing the draft box holds is dropped without a word: it is added, or the box says why not.
            if (charCount(t) > GAP_MAX_CHARS) {
              note({ en: `${charCount(t)} characters; a note is at most ${GAP_MAX_CHARS}.`,
                ar: `${charCount(t)} حرفًا؛ الملاحظة ${GAP_MAX_CHARS} حرفًا كحد أقصى.` });
              return;
            }
            if (items.includes(t)) {
              note({ en: 'That note is already listed.', ar: 'هذه الملاحظة موجودة بالفعل.' });
              return;
            }
            writeGaps(lang, [...items, t]);
            setGapDraft((d) => ({ ...d, [lang]: '' }));
            note(null);
          };
          return (
            <Row key={lang} dir={lang === 'ar' ? 'rtl' : undefined}>
              <State>{lang === 'en' ? 'English' : 'العربية'} ({items.length}/{GAP_MAX_ITEMS})</State>
              {items.map((x, i) => (
                // eslint-disable-next-line react/no-array-index-key -- a stored list may repeat a note
                <Chip key={`${i}-${x}`} $checked as="span" lang={lang}>
                  {x}
                  <Remove type="button" disabled={disabled} aria-label={`remove ${x}`}
                    onClick={() => writeGaps(lang, items.filter((_, j) => j !== i))}>×</Remove>
                </Chip>
              ))}
              <Input type="text" lang={lang} style={{ width: 220 }}
                disabled={disabled || items.length >= GAP_MAX_ITEMS}
                aria-label={lang === 'en' ? 'add an English note' : 'add an Arabic note'}
                value={gapDraft[lang] || ''}
                onChange={(e) => { setGapDraft((d) => ({ ...d, [lang]: e.target.value })); note(null); }}
                onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); add(); } }} />
              <SmallButton type="button" disabled={disabled || !(gapDraft[lang] || '').trim()} onClick={add}
                aria-label={lang === 'en' ? 'Add English note' : 'Add Arabic note'}>
                <Both en="Add" ar="إضافة" />
              </SmallButton>
              {gapNote[lang] && <Note $tone="warning" role="status"><Both {...(gapNote[lang] as Bilingual)} /></Note>}
            </Row>
          );
        })}
        <Row>{defaultButton('taxonomy_gaps')}</Row>
        <Errors errors={errorsFor('taxonomy_gaps')} />
      </Setting>

      {/* census_min_count */}
      <Setting $error={errorsFor('census_min_count').length > 0}>
        <Heading en="Census privacy floor" ar="الحد الأدنى لخصوصية التعداد" k="config.census_min_count"
          usingDefault={absent('census_min_count') ? { en: 'not set: no floor', ar: 'غير مضبوط: لا حد أدنى' } : undefined} />
        <Help>
          A privacy setting. The Who page and the Brief show census shares such as &quot;12% are children&quot;. When the
          group behind a share is very small, the share, or the rest of the residents it implies, can point to
          particular households. Set a number of people here and GUS withholds any share whose own count, or the
          count of everyone else, is below it. Leave it empty for no floor, which is GUS&apos;s default.
        </Help>
        <Row>
          <NumberInput type="text" inputMode="numeric" disabled={disabled} placeholder="people"
            aria-label="census privacy floor, in people"
            value={floor === null || floor === undefined ? '' : String(floor)}
            onChange={(e) => set('census_min_count', parseCount(e.target.value))} />
          <State><Both en="people" ar="شخصًا" /></State>
          {defaultButton('census_min_count')}
        </Row>
        <Errors errors={errorsFor('census_min_count')} />
      </Setting>

      {/* place_quality_raised */}
      <Setting $error={errorsFor('place_quality_raised').length > 0}>
        <Heading en="Raised reasons about the place" ar="شكاوى تخص المكان نفسه" k="config.place_quality_raised"
          usingDefault={absent('place_quality_raised') ? { en: 'not set: none labelled', ar: 'غير مضبوط: لا تصنيف' } : undefined} />
        <Help>
          On the Why page, which complaints are about the place itself (its quality) rather than its service. Those
          not chosen count as service. With none chosen, the Why page labels no complaint either way.
        </Help>
        <Row>
          {RAISED_REASONS.map((r) => (
            <Chip key={r.id} $checked={pq.includes(r.id)}>
              <HiddenCheckbox type="checkbox" checked={pq.includes(r.id)} disabled={disabled}
                onChange={() => togglePq(r.id)} aria-label={r.en} />
              {r.en} <Arabic lang="ar" dir="rtl">{r.ar}</Arabic>
            </Chip>
          ))}
          {defaultButton('place_quality_raised')}
        </Row>
        <Errors errors={errorsFor('place_quality_raised')} />
      </Setting>
    </Panel>
  );
};

/** The census fields, as the Who page means them. */
const CENSUS_FIELD_LABELS: Record<string, { en: string; ar: string }> = {
  residents: { en: 'Residents', ar: 'السكان' },
  male: { en: 'Male', ar: 'الذكور' },
  female: { en: 'Female', ar: 'الإناث' },
  citizen: { en: 'Citizens (count or %)', ar: 'المواطنون (عدد أو نسبة)' },
  children: { en: 'Children', ar: 'الأطفال' },
  jobs: { en: 'Jobs', ar: 'الوظائف' },
  employment: { en: 'Employment', ar: 'التوظيف' },
};

export default SatisfactionSettingsPanel;
